import { useState, useRef, useEffect, type CSSProperties, type ReactNode } from 'react';

export interface DropdownItem {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  onSelect?: (value: string) => void;
  className?: string;
}

export function Dropdown({ trigger, items, onSelect, className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const menuStyle: CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 6,
    backgroundColor: 'var(--theme-surface, #14141c)',
    border: '1px solid var(--theme-border-light, rgba(255,255,255,0.1))',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
    zIndex: 100,
    minWidth: 170,
    overflow: 'hidden',
    display: open ? 'block' : 'none',
    animation: open ? 'fadeInDown 0.15s ease' : undefined,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  };

  const itemStyle = (disabled?: boolean): CSSProperties => ({
    display: 'block',
    width: '100%',
    padding: '10px 14px',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    fontSize: 14,
    color: 'var(--theme-text, #eeeef0)',
    transition: 'background-color 0.15s',
  });

  return (
    <div ref={ref} className={className} style={{ position: 'relative', display: 'inline-block' }}>
      <div onClick={() => setOpen(!open)} data-testid="dropdown-trigger">
        {trigger}
      </div>
      <div role="menu" style={menuStyle}>
        {items.map((item) => (
          <button
            key={item.value}
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              if (!item.disabled) {
                onSelect?.(item.value);
                setOpen(false);
              }
            }}
            onMouseEnter={(e) => {
              if (!item.disabled) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--theme-surface-elevated, #1e1e28)';
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '';
            }}
            style={itemStyle(item.disabled)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
